// src/contracts/ops/OpsEventGuards.ts
//
// Lightweight runtime guards for Ops Events.
// - Designed for production use (cheap)
// - Rejects malformed events early
//

import type {
  SynkOpsMetricDetailV1,
  SynkOpsViolationDetailV1,
  SynkOpsCapabilityDetailV1,
  SynkOpsActionDetailV1,
  SynkWiringReadyDetailV1,
  SynkOpsTimelineDetailV1,
} from "./OpsEvents";

const isObj = (x: unknown): x is Record<string, any> =>
  typeof x === "object" && x !== null;

export function isWiringReadyV1(x: unknown): x is SynkWiringReadyDetailV1 {
  return (
    isObj(x) &&
    x.v === 1 &&
    typeof x.at === "number" &&
    typeof x.buildId === "string" &&
    typeof x.manifestChecksum === "string" &&
    isObj(x.authoritySource) &&
    typeof x.authoritySource.id === "string" &&
    Array.isArray(x.installed)
  );
}

export function isOpsMetricV1(x: unknown): x is SynkOpsMetricDetailV1 {
  return (
    isObj(x) &&
    x.v === 1 &&
    typeof x.at === "number" &&
    typeof x.kind === "string" &&
    typeof x.name === "string"
  );
}

export function isOpsViolationV1(x: unknown): x is SynkOpsViolationDetailV1 {
  return (
    isObj(x) &&
    x.v === 1 &&
    typeof x.at === "number" &&
    typeof x.severity === "string" &&
    typeof x.channel === "string" &&
    typeof x.where === "string" &&
    typeof x.reason === "string"
  );
}

export function isOpsCapabilityV1(
  x: unknown,
): x is SynkOpsCapabilityDetailV1 {
  return (
    isObj(x) &&
    x.v === 1 &&
    typeof x.at === "number" &&
    typeof x.kind === "string" &&
    typeof x.id === "string" &&
    typeof x.status === "string" &&
    typeof x.tierRequired === "string" &&
    typeof x.safety === "string"
  );
}

export function isOpsActionV1(x: unknown): x is SynkOpsActionDetailV1 {
  return (
    isObj(x) &&
    x.v === 1 &&
    typeof x.at === "number" &&
    typeof x.actor === "string" &&
    typeof x.action === "string"
  );
}

export function isOpsTimelineV1(x: unknown): x is SynkOpsTimelineDetailV1 {
  return (
    isObj(x) &&
    x.v === 1 &&
    typeof x.at === "number" &&
    typeof x.kind === "string" &&
    isObj(x.detail)
  );
}
